import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type EventArg,
  type NavigationAction,
  useNavigation,
} from '@react-navigation/native';
import {
  useCustomizationContext,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { useDynamicFeatureFlags } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook to handle the "exit customization" process.
 *
 * @see https://reactnavigation.org/docs/6.x/preventing-going-back/
 */
export const useConfirmExitCustomization = (params: Params) => {
  const { actor } = params;

  const { track } = useTelemetry();
  const navigation = useNavigation();

  const { isConfirmExitEnabled } = useDynamicFeatureFlags();

  // ─── Context ───────────────────────────────────────────────────────────────

  const { product, modifications, modifiersCount } = useCustomizationContext({
    actor,
  });

  // ─── State ─────────────────────────────────────────────────────────────────

  const [isConfirmExitModalVisible, setIsConfirmExitModalVisible] = useState(false); // prettier-ignore

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const navigationExitActionRef = useRef<NavigationAction>(null);

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const hasActiveModifications = Boolean(product && modifications && modifiersCount > 0); // prettier-ignore

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const onExitModalShow = useCallback(() => {
    track('customization.exit-modal.shown');
  }, [track]);

  const cancelExit = useCallback(() => {
    track('customization.exit-modal.stay-on-page-tapped');
    setIsConfirmExitModalVisible(false);
  }, [track]);

  const confirmExit = useCallback(() => {
    track('customization.exit-modal.leave-page-tapped');
    setIsConfirmExitModalVisible(false);

    if (!navigationExitActionRef.current) return;

    // Dispatch the navigation action to exit the customization screen
    navigation.dispatch(navigationExitActionRef.current);
  }, [navigation, track]);

  const handleBeforeRemove = useCallback(
    (event: NavigationEvent) => {
      if (!hasActiveModifications) return;

      event.preventDefault();

      // Store the navigation action to be dispatched when the user confirms the exit
      navigationExitActionRef.current = event.data.action;

      setIsConfirmExitModalVisible(true);
    },
    [hasActiveModifications],
  );

  const listenForBeforeRemove = useCallback(() => {
    if (!isConfirmExitEnabled) return;

    return navigation.addListener('beforeRemove', handleBeforeRemove);
  }, [handleBeforeRemove, navigation, isConfirmExitEnabled]);

  /**
   * Toggles gesture navigation based on active modifications.
   */
  const toggleGestureEnabled = useCallback(() => {
    if (!isConfirmExitEnabled) return;

    const parentStack = navigation.getParent();

    if (!parentStack) return;

    parentStack.setOptions({ gestureEnabled: !hasActiveModifications });
  }, [navigation, isConfirmExitEnabled, hasActiveModifications]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(listenForBeforeRemove, [listenForBeforeRemove]);
  useEffect(toggleGestureEnabled, [toggleGestureEnabled]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isConfirmExitModalVisible,
    onExitModalShow,
    cancelExit,
    confirmExit,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
};

type NavigationEvent = EventArg<
  'beforeRemove',
  true,
  { action: NavigationAction }
>;
